import { createUrl } from "@/redux/api";
import {
  BaseOrganizationResponse,
  DeleteOrganizationRequest,
  OrganizationResponse,
  OrganizationQueryParams,
  SubjectResponse,
  SubjectQueryParams,
  Tags,
  TagsType,
  StartOrganizationExportResponse,
  OrganizationImportResponse,
  OrganizationImportRequest,
  DeleteOrganizationResponse,
  OrganizationExportDownload,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  DelistOrganizationResponse,
} from "@/redux/organizations/types";
import { convertToFormData } from "@/shared/utils/forms";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { BuilderType, PaginatedResponse } from "@/redux/types";

const organizationsService = (builder: BuilderType<Tags>) => ({
  getOrganizations: builder.query<
    BaseOrganizationResponse[] | PaginatedResponse<BaseOrganizationResponse>,
    OrganizationQueryParams
  >({
    query: (queryParams = {}) => ({
      url: createUrl("/organizations/", queryParams),
      method: "GET",
    }),
    providesTags: (result): TagsType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as BaseOrganizationResponse[]
            ).map(
              (i): TagsType => ({
                type: "Organization",
                id: i.id,
              }),
            ),
            { type: "Organizations" },
          ]
        : [{ type: "Organizations" }],
  }),
  getOrganizationById: builder.query<
    BaseOrganizationResponse | OrganizationResponse,
    number | string
  >({
    query: (organizationId) => ({
      url: `/organizations/${organizationId}/`,
      method: "GET",
    }),
    providesTags: (result) => {
      return result
        ? [{ type: "Organization", id: result.id }, { type: "Organizations" }]
        : [{ type: "Organizations" }];
    },
  }),
  createOrganization: builder.mutation<
    OrganizationResponse,
    CreateOrganizationRequest
  >({
    query: (data) => ({
      url: "/organizations/",
      method: "POST",
      data,
    }),
    invalidatesTags: [{ type: "Organizations" }],
  }),
  updateOrganizationById: builder.mutation<
    OrganizationResponse,
    { id: number; data: UpdateOrganizationRequest }
  >({
    query: ({ id, data }) => ({
      url: `/organizations/${id}/`,
      method: "PUT",
      data: convertToFormData(data),
    }),
    invalidatesTags: (result) =>
      result
        ? [{ type: "Organizations" }, { type: "Organization", id: result.id }]
        : [],
  }),
  patchOrganizationById: builder.mutation<
    OrganizationResponse,
    { id: number; data: Partial<UpdateOrganizationRequest> }
  >({
    query: ({ id, data }) => ({
      url: `/organizations/${id}/`,
      method: "PATCH",
      data: convertToFormData(data),
    }),
    invalidatesTags: (result) =>
      result
        ? [{ type: "Organizations" }, { type: "Organization", id: result.id }]
        : [],
  }),
  deleteOrganizationById: builder.mutation<
    null | DeleteOrganizationResponse,
    { id: number; data?: DeleteOrganizationRequest }
  >({
    query: ({ id, data = null }) => ({
      url: `/organizations/${id}/`,
      method: "DELETE",
      data,
    }),
    invalidatesTags: (result, error, { id }) => [
      { type: "Organizations" },
      { type: "Organization", id },
    ],
  }),
  importOrganizationsFromCsv: builder.mutation<
    OrganizationImportResponse,
    OrganizationImportRequest
  >({
    query: (data) => ({
      url: `/organizations/import/`,
      method: "POST",
      data,
    }),
    invalidatesTags: (result) => (result ? [{ type: "Organizations" }] : []),
  }),
  exportOrganizationData: builder.mutation<
    StartOrganizationExportResponse,
    number | string
  >({
    query: (id) => ({
      url: `/organizations/${id}/export/`,
      method: "POST",
    }),
  }),
  getExportedOrganizationData: builder.query<
    OrganizationExportDownload,
    number | string
  >({
    query: (id) => ({
      url: `/organizations/${id}/export/`,
      method: "GET",
    }),
  }),
  delistOrganization: builder.mutation<
    DelistOrganizationResponse,
    number | string
  >({
    query: (id) => ({
      url: `/organizations/${id}/delist/`,
      method: "POST",
    }),
    invalidatesTags: (result) => (result ? [{ type: "Organizations" }] : []),
  }),
  getSubjects: builder.query<
    SubjectResponse[] | PaginatedResponse<SubjectResponse>,
    SubjectQueryParams
  >({
    query: (filters) => createUrl("/subjects/", filters),
  }),
});

export default organizationsService;
